import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Persisted keys — the primary auth mechanism is an HttpOnly cookie set by the
// backend, but some environments (mobile Safari/Chrome in cross-site iframes,
// Kubernetes ingress with wildcard CORS) block third-party cookies. We keep a
// sessionStorage Bearer fallback that the backend also accepts via the
// `Authorization: Bearer` header. `viewingPatient` is UI state and is
// re-validated server-side on every request.
export const VIEWING_PATIENT_KEY = "renalos_viewing_patient";
const AUTH_TOKEN_KEY = "renalos_auth_token";  // sessionStorage only

export function getAuthToken() {
  try { return sessionStorage.getItem(AUTH_TOKEN_KEY) || null; }
  catch { return null; }
}
export function setAuthToken(token) {
  try {
    if (token) sessionStorage.setItem(AUTH_TOKEN_KEY, token);
    else sessionStorage.removeItem(AUTH_TOKEN_KEY);
  } catch { /* private mode */ }
}

// Global "viewing patient" — when a provider clicks a patient card, we set this
// id and every request from the shared pages auto-scopes to that patient.
let viewingPatientId = null;

function _rehydrateViewingId() {
  try {
    const raw = localStorage.getItem(VIEWING_PATIENT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.patient_id) viewingPatientId = parsed.patient_id;
    }
  } catch { /* ignore corrupted value */ }
}
_rehydrateViewingId();

export function setViewingPatientId(id) { viewingPatientId = id || null; }
export function getViewingPatientId() { return viewingPatientId; }

export function persistViewingPatient(patient) {
  if (patient && patient.patient_id) {
    localStorage.setItem(VIEWING_PATIENT_KEY, JSON.stringify(patient));
  } else {
    localStorage.removeItem(VIEWING_PATIENT_KEY);
  }
}
export function readPersistedViewingPatient() {
  try {
    const raw = localStorage.getItem(VIEWING_PATIENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// `withCredentials: true` makes the browser attach the HttpOnly auth cookie on
// every request when the CORS response is spec-compliant. When it's not (e.g.
// mobile browsers behind Kubernetes ingress that wildcards CORS), the request
// interceptor below falls back to a Bearer token stored in sessionStorage.
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (viewingPatientId) {
    config.params = { ...(config.params || {}), patient_id: viewingPatientId };
  }
  const token = getAuthToken();
  if (token) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return config;
});
